import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { changesApi } from '../../../services/changesApi';
import type {
  AgendaKind,
  AgendaLayoutKey,
  ChangeMeetingAgendaEmailPreview,
} from '../../../types/changes';
import { catalogHubPath } from '../catalogRegistry';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  ATTACHMENT_ACCEPT,
  MAX_EMAIL_ATTACHMENTS,
  validateEmailAttachmentFile,
} from '../../../utils/uploadValidation';

export interface MeetingAgendaComposeSendPayload {
  subject: string;
  bodyHtml: string;
  files: File[];
}

interface Props {
  show: boolean;
  meetingId: number;
  agendaKind: AgendaKind;
  layout?: AgendaLayoutKey;
  sending?: boolean;
  onHide: () => void;
  onSend: (payload: MeetingAgendaComposeSendPayload) => void;
}

export function htmlToEditableText(html: string): string {
  if (!html) return '';
  const text = html
    .replace(/\r\n/g, '\n')
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|h[1-6]|li|tr)\s*>/gi, '\n')
    .replace(/<\s*\/\s*ul\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return text
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function editableTextToHtml(text: string): string {
  const trimmed = (text || '').trim();
  if (!trimmed) return '';
  const escaped = trimmed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    .split(/\n{2,}/)
    .map((block) => `<p>${block.replace(/\n/g, '<br>\n')}</p>`)
    .join('\n');
}

function composeEditableDescription(bodyHtml: string, pautaHtml: string): string {
  const pauta = (pautaHtml || '').trim();
  const body = bodyHtml || '';
  const alreadyHas = Boolean(pauta && body.includes(pauta));
  const source = alreadyHas || !pauta ? body : `${body}\n${pauta}`;
  return htmlToEditableText(source);
}

const MeetingAgendaComposeModal: React.FC<Props> = ({
  show,
  meetingId,
  agendaKind,
  layout = 'auto',
  sending = false,
  onHide,
  onSend,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ChangeMeetingAgendaEmailPreview | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  const applyDraft = (response: ChangeMeetingAgendaEmailPreview) => {
    setTitle(response.subject || '');
    setDescription(composeEditableDescription(response.body_html || '', response.pauta_html || ''));
  };

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await changesApi.previewMeetingAgendaEmail(meetingId, {
        agenda_kind: agendaKind,
        layout,
      });
      setPreview(response);
      applyDraft(response);
    } catch (err: unknown) {
      setPreview(null);
      setError(getApiErrorMessage(err, 'Falha ao puxar o layout do e-mail da pauta.'));
    } finally {
      setLoading(false);
    }
  }, [agendaKind, layout, meetingId]);

  useEffect(() => {
    if (show) {
      setFiles([]);
      void loadPreview();
    }
  }, [loadPreview, show]);

  const restoreLayout = () => {
    if (!preview) return;
    applyDraft(preview);
  };

  const addFiles = (list: FileList | null) => {
    if (!list?.length) return;
    const next = [...files];
    for (const file of Array.from(list)) {
      const invalid = validateEmailAttachmentFile(file);
      if (invalid) {
        setError(invalid);
        continue;
      }
      if (next.length >= MAX_EMAIL_ATTACHMENTS) {
        setError(`No máximo ${MAX_EMAIL_ATTACHMENTS} arquivos por e-mail.`);
        break;
      }
      if (next.some((item) => item.name === file.name && item.size === file.size)) {
        continue;
      }
      next.push(file);
    }
    setFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const previewHtml = editableTextToHtml(description);
  const canSend = Boolean(title.trim() && description.trim()) && !loading && !sending;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      backdrop="static"
      scrollable
      centered
      animation={false}
      enforceFocus={false}
      restoreFocus={false}
      container={typeof document !== 'undefined' ? document.body : undefined}
      style={{ zIndex: 1075 }}
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title as="h2">Escrever e-mail da reunião</Modal.Title>
          <div className="small text-muted mt-1">
            A pauta entra na descrição para você editar, apagar ou mudar o texto antes de enviar.
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {error ? (
          <Alert variant="danger" className="py-2" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {loading ? <div className="text-muted small mb-2">Puxando o layout e a pauta desta reunião…</div> : null}
        {preview && !preview.layout_enabled ? (
          <Alert variant="warning" className="py-2 small">
            O layout «{preview.layout_label}» está desligado no cadastro. Você ainda pode escrever e
            enviar este texto agora.
          </Alert>
        ) : null}
        <div className="small mb-3">
          Layout:{' '}
          <strong>{preview?.layout_label || 'cadastro'}</strong>
          {' · '}
          <Link to={catalogHubPath('email-pauta')}>Abrir cadastro E-mail da pauta</Link>
        </div>
        <Form.Label className="small">Título</Form.Label>
        <Form.Control
          className="mb-2"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          aria-label="Título do e-mail da reunião"
          disabled={loading}
        />
        <Form.Label className="small">Descrição</Form.Label>
        <p className="small text-muted mb-1">
          Inclui o texto do cadastro e as mudanças da pauta. Apague ou edite as linhas que não quiser
          enviar.
        </p>
        <Form.Control
          as="textarea"
          rows={16}
          className="mb-3"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          aria-label="Descrição do e-mail da reunião"
          disabled={loading}
        />
        <Form.Label className="small d-block">Anexos</Form.Label>
        <p className="small text-muted mb-2">
          Clique no botão para escolher o arquivo que vai junto no e-mail (PDF, Word, Excel, PPT ou
          imagem).
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          className="d-none"
          aria-label="Selecionar arquivos para anexar"
          disabled={loading || sending}
          onChange={(event) => addFiles(event.target.files)}
        />
        <Button
          size="sm"
          variant="outline-primary"
          className="mb-2"
          disabled={loading || sending}
          onClick={() => fileInputRef.current?.click()}
        >
          Anexar arquivo
        </Button>
        {files.length ? (
          <ul className="small mb-3 ps-3">
            {files.map((file, index) => (
              <li key={`${file.name}-${file.size}-${index}`} className="mb-1">
                {file.name}
                <Button
                  size="sm"
                  variant="link"
                  className="py-0 ms-1"
                  onClick={() => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                >
                  Remover
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="small text-muted mb-3">Nenhum arquivo anexado.</div>
        )}
        <Form.Label className="small">Prévia</Form.Label>
        <iframe
          title="Prévia do e-mail da reunião"
          sandbox=""
          srcDoc={previewHtml || '<p></p>'}
          style={{
            width: '100%',
            minHeight: 180,
            border: '1px solid #dee2e6',
            borderRadius: 6,
            background: '#fff',
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm" variant="outline-secondary" onClick={restoreLayout} disabled={!preview || sending}>
          Restaurar layout
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={onHide} disabled={sending}>
          Cancelar
        </Button>
        <Button
          size="sm"
          disabled={!canSend}
          onClick={() =>
            onSend({ subject: title.trim(), bodyHtml: previewHtml, files })
          }
        >
          Enviar este texto
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MeetingAgendaComposeModal;
